<template>
	<div class="section--right">
		<Login
			v-focus-section
			v-if="
				(isLoggedIn === false && width > 960 && !isSearching) ||
				(isLoggedIn === false && width <= 960 && !isSearching && !isMobileDevice)
			"
		/>
		<LangSwitcher v-focus v-if="width > 960 && !isSearching" />
		<Search @search-opened="hasStartedSearching()" @search-closed="hasFinishedSearching()" v-focus />
		<profile v-focus v-if="isLoggedIn === true && width > 960" />

		<img
			v-if="!isLoggedIn && width > 960"
			@click="() => triggerLogin()"
			class="login-avatar"
			src="../../images/login_avatar.webp"
			alt="Login"
			v-tooltip.bottom="'Σύνδεση'"
		/>

		<div id="mobilemenu" ref="mobileMenu" class="mobile-menu" v-if="width <= 960 && !isSearching">
			<button @click="toggleMenuVisibility" aria-haspopup="true" aria-controls="overlay_menu">
				<IconMenu :open="isBurgerMenuOpen"></IconMenu>
			</button>
			<TieredMenu ref="menu" id="overlay_menu" :model="items" :popup="true" appendTo="#mobilemenu">
				<template #item="{ hasSubmenu, item, props }">
					<button v-if="item.id === 'register_menu_item'" class="btn red tiered-menu--button" @click="item.command">
						{{ item.label }}
					</button>
					<a v-else class="flex items-center" v-bind="props.action">
						<span>{{ item.label }}</span>
						<i v-if="hasSubmenu" class="pi pi-angle-right ml-auto"></i>
					</a>
				</template>
			</TieredMenu>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { computed, ComputedRef, ref } from "vue"
	import TieredMenu from "primevue/tieredmenu"
	import IconMenu from "../../icons/IconMenu.vue"
	import { useUserStore } from "../../stores/user"
	import { useLogin } from "../../composables/useLogin"
	import LangSwitcher from "../header/LangSwitcher.vue"
	import Search from "../Search.vue"
	import profile from "../profileBlock.vue"
	import { useWindowSize, onClickOutside } from "@vueuse/core"
	import { useI18n } from "vue-i18n"
	import { isNativeDevice, url } from "../../helper"
	import { useRouter } from "vue-router"
	import { useLogout } from "../../composables/useLogout"
	import Login from "../LoginBlock.vue"
	import { useDeviceManager } from "../../stores/deviceManager"
	import { Browser } from "@capacitor/browser"
	import { Capacitor } from "@capacitor/core"
	import api from "../../composables/useApi"
	import { useCurrentUser } from "../../composables/useCurrentUser"
	import { refreshTokenIfNeeded } from "../../composables/useAuth"
	const { login } = useLogin()
	const deviceManager = useDeviceManager()
	const userStore = useUserStore()
	const isSearching = ref(false)
	const emit = defineEmits<{
		(e: "search-opened"): void
		(e: "search-closed"): void
	}>()
	/**
	 * A computed property to check if the user is logged in.
	 */
	const isLoggedIn: ComputedRef<boolean> = computed((): boolean => userStore.state.isLoggedIn)
	const isMobileDevice = computed(
		() =>
			deviceManager.operatingSystem === "ios" ||
			deviceManager.operatingSystem === "android" ||
			Capacitor.isNativePlatform()
	)
	const { logout } = useLogout()
	const router = useRouter()
	const { t, locale } = useI18n()
	const { fetchCurrentUser } = useCurrentUser(locale)

	const toggleMenuVisibility = (event: any) => {
		menu.value.toggle(event)
		isBurgerMenuOpen.value = !isBurgerMenuOpen.value
	}

	const { width, height } = useWindowSize()
	const menu = ref()
	const mobileMenu = ref()
	const isBurgerMenuOpen = ref(false)

	// Close menu when clicking outside
	onClickOutside(mobileMenu, (e) => {
		if (e.target.innerText === t("header.languageSelector")) {
			return
		}
		if (isBurgerMenuOpen.value) {
			menu.value.hide()
			isBurgerMenuOpen.value = false
		}
	})

	const languages = [
		{ code: "en", label: "English" },
		{ code: "el", label: "Ελληνικά" },
		// Add more languages as needed
	]

	const hasFinishedSearching = () => {
		isSearching.value = false
		emit("search-closed")
	}

	const hasStartedSearching = () => {
		isSearching.value = true
		emit("search-opened")
	}

	async function switchLang(langCode: string) {
		if (userStore.state.isLoggedIn) {
			await api
				.patch(
					`${url}/jsonapi/user/user/${userStore.state.user.id}`,
					{
						data: {
							type: "user--user",
							id: userStore.state.user.id,
							attributes: {
								preferred_langcode: langCode,
							},
						},
					},
					{
						headers: { "Content-Type": "application/vnd.api+json", Accept: "application/vnd.api+json" },
					}
				)
				.then(async (response) => {
					// console.log("Successfully updated preferred language:", response.data)
					localStorage.setItem("preferred_langcode", langCode)
				})
				.catch((error) => {
					console.error("Error updating preferred language:", error)
				})
		}

		localStorage.removeItem("jwt")
		await refreshTokenIfNeeded()
		const newurl = new URL(window.location.href)
		newurl.searchParams.set("lang", langCode)
		window.location.href = newurl.toString()
	}

	const items = ref([
		{
			label: t("header.frontpage"),
			visible: Capacitor.isNativePlatform() === false,
			command: (event: any) => {
				toggleMenuVisibility(event)
				router.push({ name: "Home" })
			},
		},
		{
			label: t("header.movies"),
			visible: Capacitor.isNativePlatform() === false,
			command: (event: any) => {
				toggleMenuVisibility(event)
				router.push({ name: "MoviesPage" })
			},
		},
		{
			label: t("header.series"),
			visible: Capacitor.isNativePlatform() === false,
			command: (event: any) => {
				toggleMenuVisibility(event)
				router.push({ name: "SeriesPage" })
			},
		},
		// {
		// 	label: "Κατηγορίες",
		// 	command: () => {
		// 		toggleMenuVisibility()
		// 		router.push({ name: "CategoryPage" })
		// 	},
		// },
		{
			label: t("header.languageSelector"),
			id: "lang_switcher",
			items: [
				...languages.map((lang) => ({
					label: lang.label,
					command: () => {
						switchLang(lang.code)
					},
					icon: "",
				})),
			],
		},
		{
			separator: true,
		},
		{
			label: t("profile.login"),
			command: async (event: any) => {
				toggleMenuVisibility(event)
				await triggerLogin()
			},
			visible: computed(() => !userStore.state.isLoggedIn),
		},
		{
			id: "register_menu_item",
			label: t("profile.register"),
			command: async (event: any) => {
				toggleMenuVisibility(event)
				await triggerLogin()
			},
			visible: computed(() => !userStore.state.isLoggedIn && Capacitor.isNativePlatform() === false),
		},

		{
			label: t("devices.title"),
			command: (event: any) => {
				toggleMenuVisibility(event)
				router.push({ name: "DeviceManagement" })
			},
			visible: computed(() => userStore.state.isLoggedIn),
		},
		{
			label: t("profile.myProfile"),
			command: (event: any) => {
				toggleMenuVisibility(event)
				router.push("/profile")
			},
			visible: computed(() => userStore.state.isLoggedIn),
		},
		{
			label: t("profile.myAccount"),
			command: async (event: any) => {
				toggleMenuVisibility(event)
				if (Capacitor.isNativePlatform()) {
					await Browser.open({
						url: `${url}/${locale.value}/user/${userStore.state.user.drupal_internal__uid}?destination=valormovies://callback&mobile_app=1`,
					})
				} else {
					window.location.href = `${url}/${locale.value}/user`
				}
			},
			visible: computed(() => userStore.state.isLoggedIn),
		},
		{
			separator: true,
			visible: computed(() => userStore.state.isLoggedIn),
		},
		{
			label: t("profile.logout"),
			command: () => {
				toggleMenuVisibility()
				logout()
			},
			visible: computed(() => userStore.state.isLoggedIn),
		},
	])

	/**
	 * A small helper to trigger the login process.
	 */
	const triggerLogin = async (): Promise<void> => {
		sessionStorage.setItem("post_auth_redirect", "/add-profile")
		await login()
	}
</script>

<style lang="scss" scoped>
	.section--right {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 40px;

		@include br.respond-to(br.$desktop-medium) {
			gap: 20px;
		}
	}
	:global(.p-tieredmenu-item-content) {
		text-align: center;
	}
</style>

<style lang="scss">
	.mobile-menu {
		position: relative;
	}
	#overlay_menu {
		background: var(--black) !important;
	}
	.p-tieredmenu-submenu {
		background: var(--black) !important;
	}
	.p-tieredmenu-item:not(.p-disabled) > .p-tieredmenu-item-content:hover {
		background: var(--dark-gray) !important;
	}

	#overlay_menu {
		transform: translateY(12px);
		right: 0px !important;
		left: unset !important;
		top: 30px !important;
		inset-inline-start: unset !important;
	}
	// @include br.respond-to(br.$mobile-xlarge) {
	// #overlay_menu {
	// 	right: 0px !important;
	// 	left: unset !important;
	// 	top: 30px !important;
	// 	inset-inline-start: unset !important;
	// }
	// }
	.main-menu {
		margin: 0px;
		padding: 0px;
		max-width: unset;
		width: 100%;
		display: flex;
		align-items: center;
		gap: 72px;
		justify-content: space-between;
		margin: 0 auto;
		width: auto;
		li {
			list-style: none;
		}
		a {
			&:not(.p-tieredmenu-item-link) {
				text-decoration: none;
				padding: 3px 16px;
				border-radius: 16px;
				line-height: 1;
				text-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
				font-size: 24px;
				&.router-link-exact-active,
				&:focus-visible,
				&:focus-within,
				&:focus,
				&:hover {
					background: white;
					color: black;
					text-shadow: none;
				}
			}
		}
	}

	.login-avatar {
		width: 45px;
		height: 45px;
		border-radius: 50%;
		object-fit: cover;
		object-position: center;
		background: var(--gray);
		cursor: pointer;

		@include br.respond-to(br.$desktop-medium) {
			width: 32px;
			height: 32px;
		}
	}
</style>
